import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms'
import { SnoballService } from '@app/v2/event/services/snoball.service'
import { EventApp } from '@app/v2/event/types/event-app.type'
import { CreateSnoballConfigurationPayload, SnoballConfiguration } from '@app/v2/event/types/snoball.type'
import { Persona } from '@app/v2/personas/types/persona.type'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { finalize } from 'rxjs'

@Component({
  selector: 'app-snoball-config-form-modal',
  standalone: true,
  imports: [ModalComponent, ReactiveFormsModule, BasicInputComponent, SelectInputComponent, ButtonSpinnerDirective, TranslateModule],
  templateUrl: './snoball-config-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SnoballConfigFormModalComponent {
  public readonly eventApp = input<EventApp | null>(null)
  public readonly eventId = input<number | null>(null)
  public readonly availablePersonas = input<Persona[]>([])
  public readonly configuration = input<SnoballConfiguration | null>(null)
  public readonly onCloseModal = output<void>()
  public readonly saved = output<void>()

  public readonly modalId = 'snoball-config-form-modal'
  public readonly isSaving = signal(false)
  public readonly isEditing = computed(() => !!this.configuration())
  public readonly headerIcon = computed(() => this._snoballService.headerIconHtml(this.eventApp()))
  /**
   * Caps the picker without collapsing it to one choice. Unused personas are a subset of the event,
   * so a single leftover would otherwise set maxSelections to 1 and the shared select would flip
   * to single-select (`Visit Persona/s *` instead of chips).
   */
  public readonly personaMaxSelections = computed(() => Math.max(this.availablePersonas().length, 2))
  public readonly personaIds = signal(new FormControl<number[]>([], { nonNullable: true, validators: [this._minArrayLength(1)] }))
  public readonly projectId = signal(new FormControl('', { nonNullable: true, validators: [this._requiredText] }))
  public readonly campaignId = signal(new FormControl('', { nonNullable: true, validators: [this._requiredText] }))
  public readonly form = signal(
    new FormGroup({
      persona_ids: this.personaIds(),
      project_id: this.projectId(),
      campaign_id: this.campaignId()
    })
  )

  private readonly _snoballService = inject(SnoballService)
  private readonly _alertService = inject(AlertService)
  private readonly _modalService = inject(ModalService)
  private readonly _translate = inject(TranslateService)
  private readonly _destroyRef = inject(DestroyRef)

  constructor() {
    effect(() => {
      const config = this.configuration()
      if (config) {
        this.form().reset({
          persona_ids: [...config.persona_ids],
          project_id: config.project_id,
          campaign_id: config.campaign_id
        })
        return
      }
      this.resetForm()
    })
  }

  /**
   * Resets the form when the modal is dismissed without saving.
   */
  public resetForm(): void {
    this.form().reset({ persona_ids: [], project_id: '', campaign_id: '' })
    this.form().markAsPristine()
    this.form().markAsUntouched()
  }

  /**
   * Resets the form when the modal is dismissed.
   */
  public onDismiss(): void {
    this.resetForm()
    this.onCloseModal.emit()
  }

  /**
   * Validates the form, then creates or updates a configuration and PUTs `options.snoball` on the selected personas.
   * Add and edit share this path; neither charges the wallet.
   */
  public onSave(): void {
    if (this.isSaving()) return
    this.form().markAllAsTouched()
    if (this.form().invalid) return

    const eventId = this.eventId()
    if (!eventId) return

    const payload = this._formPayload()
    if (!payload.project_id || !payload.campaign_id) return
    const editing = this.configuration()
    this.isSaving.set(true)

    const request$ = editing ? this._snoballService.updateConfiguration(eventId, editing.id, payload) : this._snoballService.addConfiguration(eventId, payload)

    request$
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this.saved.emit()
          this._alertService.alert(Alert.SUCCESS, this._translate.instant(editing ? 'v2.eventApps.snoball.editSuccess' : 'v2.eventApps.snoball.saveSuccess'))
          this.resetForm()
          this._closeModal()
          this.onCloseModal.emit()
        },
        error: (err: HttpErrorResponse) => {
          const response = (err.error ?? err) as Response<null>
          this._alertService.alert(
            Alert.ERROR,
            response?.message || this._translate.instant(this.isEditing() ? 'v2.eventApps.snoball.editError' : 'v2.eventApps.snoball.saveError')
          )
        }
      })
  }

  /**
   * Reads the current form values for create or update.
   * @returns {CreateSnoballConfigurationPayload}
   */
  private _formPayload(): CreateSnoballConfigurationPayload {
    return {
      persona_ids: this._personaIds(),
      project_id: this.projectId().value.trim(),
      campaign_id: this.campaignId().value.trim()
    }
  }

  /**
   * Normalizes the persona control to a number array (a lone remaining pick used to emit a scalar).
   * @returns {number[]}
   */
  private _personaIds(): number[] {
    const raw = this.personaIds().value as number[] | number | null | undefined
    const ids = Array.isArray(raw) ? raw : raw != null ? [raw] : []
    return ids.map(Number).filter((id) => Number.isFinite(id))
  }

  /**
   * Rejects an empty or whitespace-only ID. `Validators.required` treats spaces as a value, and save trims those to `''`.
   * @param {AbstractControl} control - Project or campaign id control.
   * @returns {ValidationErrors | null}
   */
  private _requiredText(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value.trim() : control.value
    if (value == null || value === '') return { required: true }
    return null
  }

  /**
   * Requires at least `min` selected values on a multi-select control.
   * @param {number} min - Minimum array length.
   * @returns {(control: AbstractControl) => ValidationErrors | null}
   */
  private _minArrayLength(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      const length = Array.isArray(value) ? value.length : value != null && value !== '' ? 1 : 0
      if (length < min) return { minSelections: true }
      return null
    }
  }

  /**
   * Closes this modal when it is currently open.
   */
  private _closeModal(): void {
    if (this._modalService.isModalOpen(this.modalId)) this._modalService.toggleModal(this.modalId)
  }
}
